import React, { useContext, useEffect, useState } from 'react';
import { Card, Icon, Loader, Message, Label } from 'semantic-ui-react';
import { ContactContext } from '../../contexts/contactContext';
import { RestApiContext, IGSQueryResultsResponse } from '../../contexts/restApiContext';
import { AppContext } from '../../contexts/appContext';
import { IServiceConfig } from '../../models/serviceConfigs';
import { Logger, LoggingService } from '../../services/LoggingService';

interface IGSQueryResultsProps {
  serviceConfig: IServiceConfig;
}

interface IDisplayReservation {
  number: string;
  customerName: string;
  stationId: string;
  gpbr: string;
  pickupDate: string;
  returnDate: string;
  brand: string;
}

interface IDisplayRentalAgreement {
  number: string;
  customerName: string;
  stationId: string;
  gpbr: string;
  pickupDate: string;
  returnDate: string;
  brand: string;
}

interface IGSQueryResponse {
  pagination: {
    pageSize: number;
    startRecordIndex: number;
    totalResultCount: number;
  };
  reservations?: any[];
  rentalAgreements?: any[];
}


export const GSQueryResults: React.FC<IGSQueryResultsProps> = ({ serviceConfig }) => {
  const { selectedContact, agentLang } = useContext(ContactContext);
  const { getGSQueryResults } = useContext(RestApiContext);
  const { config } = useContext(AppContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reservations, setReservations] = useState<IDisplayReservation[]>([]);
  const [rentalAgreements, setRentalAgreements] = useState<IDisplayRentalAgreement[]>([]);
  const [expandedReservations, setExpandedReservations] = useState<Set<number>>(new Set([0]));
  const [expandedRentalAgreements, setExpandedRentalAgreements] = useState<Set<number>>(new Set([0]));
  const [panelExpanded, setPanelExpanded] = useState<boolean>(true);
  const [highlightedItem, setHighlightedItem] = useState<{type: 'reservation' | 'rental' | null, item: IDisplayReservation | IDisplayRentalAgreement | null}>({type: null, item: null});

  const logger: Logger = new LoggingService().getLogger('GSQueryResults');

  // Helper function to render verification status badge
  const renderVerificationBadge = () => {
    if (!selectedContact) return null;
    
    const attributes = selectedContact.getAttributes();
    const verificationStatus = attributes?.verificationStatus?.value;
    
    // Show "Not Verified" if attribute is missing or not "Verified"
    const isVerified = verificationStatus === 'Verified';
    
    return (
      <Label 
        size="small" 
        color={isVerified ? 'green' : 'red'}
        style={{ marginLeft: '8px' }}
      >
        <Icon name={isVerified ? 'checkmark' : 'close'} style={{ marginRight: '4px' }} />
        {isVerified 
          ? (config.translate('GSQueryResults_CallerVerified', agentLang) || 'Caller match')
          : (config.translate('GSQueryResults_CallerNotVerified', agentLang) || 'Caller not matched')
        }
      </Label>
    );
  };

  // Helper function to render highlighted item section
  const renderHighlightedItem = () => {
    if (!highlightedItem.item || !highlightedItem.type) return null;

    const item = highlightedItem.item;
    const isReservation = highlightedItem.type === 'reservation';

    return (
      <Card 
        fluid 
        style={{ 
          marginBottom: '20px', 
          border: '3px solid #2185d0', 
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
      >
        <Card.Content>
          <Card.Header style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <Icon name={isReservation ? 'calendar check' : 'file alternate outline'} />
              {isReservation 
                ? (config.translate('GSQueryResults_HighlightedReservation', agentLang) || 'Reservation')
                : (config.translate('GSQueryResults_HighlightedRentalAgreement', agentLang) || 'Rental Agreement')
              }
              {' #'}{item.number}
            </div>
            {renderVerificationBadge()}
          </Card.Header>
          
          <Card.Description>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
              <div>
                <strong>{config.translate('GSQueryResults_Customer', agentLang) || 'Customer'}:</strong>
                <div>{item.customerName}</div>
              </div>
              <div>
                <strong>{config.translate('GSQueryResults_Station', agentLang) || 'Station'}:</strong>
                <div>{item.stationId}</div>
              </div>
              <div>
                <strong>{config.translate('GSQueryResults_PickupDate', agentLang) || 'Pickup Date'}:</strong>
                <div>{item.pickupDate}</div>
              </div>
              <div>
                <strong>{config.translate('GSQueryResults_GPBR', agentLang) || 'GPBR'}:</strong>
                <div>{item.gpbr}</div>
              </div>
              <div>
                <strong>{config.translate('GSQueryResults_ReturnDate', agentLang) || 'Return Date'}:</strong>
                <div>{item.returnDate}</div>
              </div>
              <div>
                <strong>{config.translate('GSQueryResults_Brand', agentLang) || 'Brand'}:</strong>
                <div>{item.brand}</div>
              </div>
            </div>
          </Card.Description>
        </Card.Content>
      </Card>
    );
  };

  useEffect(() => {
    if (selectedContact?.contactId && serviceConfig.useGsQueryResults) {
      fetchGSQueryResults(selectedContact.contactId);
    }
  }, [selectedContact?.contactId, serviceConfig.useGsQueryResults]);

  const fetchGSQueryResults = async (contactId: string) => {
    if (!selectedContact) {
      setError('No contact selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      logger.debug('Fetching GS Query Results', { contactId });
      
      // Extract CCP token from contact attributes
      const attributes = selectedContact.getAttributes();
      const ccpToken = attributes?.CCP_TOKEN?.value;
      
      if (!ccpToken) {
        setError('CCP Token is required to fetch query results');
        return;
      }
      
      const response: IGSQueryResultsResponse = await getGSQueryResults(contactId, ccpToken);

      console.log('GS Query Results Response', response);
      
      if (response.error) {
        setError(response.error);
        return;
      }
      
      if (response.queryResults) {
        // The actual data structure is a flat array with responseType field
        const results = Array.isArray(response.queryResults.results) ? response.queryResults.results : [];
        
        // Filter and transform reservations for display
        const displayReservations: IDisplayReservation[] = results
          .filter((item: any) => item.responseType === 'RESERVATION')
          .map((res: any) => ({
            number: res.reservation?.reservation?.split(':').pop() || 'N/A',
            customerName: `${res.reservation?.renter?.givenName || ''} ${res.reservation?.renter?.surname || ''}`.trim() || 'N/A',
            stationId: res.reservation?.pickupInformation?.location?.station || 'N/A',
            gpbr: res.reservation?.pickupInformation?.location?.legacyId || 'N/A',
            pickupDate: res.reservation?.pickupInformation?.expectedDateTime ? new Date(res.reservation.pickupInformation.expectedDateTime).toLocaleDateString() : 'N/A',
            returnDate: res.reservation?.returnInformation?.expectedDateTime ? new Date(res.reservation.returnInformation.expectedDateTime).toLocaleDateString() : 'N/A',
            
            brand: extractBrandFromUrn(res.reservation?.brand || ''),
          }));

        // Filter and transform rental agreements for display
        const displayRentalAgreements: IDisplayRentalAgreement[] = results
          .filter((item: any) => item.responseType === 'RENTAL_AGREEMENT')
          .map((ra: any) => ({
            number: ra.rentalAgreement?.rentalAgreement?.split(':').pop() || 'N/A',
            customerName: `${ra.rentalAgreement?.renter?.givenName || ''} ${ra.rentalAgreement?.renter?.surname || ''}`.trim() || 'N/A',
            stationId: ra.rentalAgreement?.lengthOfRental?.supplyingCounter?.station || 'N/A',
            gpbr: ra.rentalAgreement?.lengthOfRental?.supplyingCounter?.legacyId || 'N/A',
            pickupDate: ra.rentalAgreement?.lengthOfRental?.pickUpDateTime ? new Date(ra.rentalAgreement.lengthOfRental.pickUpDateTime).toLocaleDateString() : 'N/A',
            returnDate: ra.rentalAgreement?.lengthOfRental?.actualReturnDateTime ? new Date(ra.rentalAgreement.lengthOfRental.actualReturnDateTime).toLocaleDateString() : 'N/A',
            
            brand: extractBrandFromUrn(ra.rentalAgreement?.brand || ''),
          }));

        // Sort by pickup date (most recent first)
        const sortedReservations = displayReservations.sort((a, b) => {
          const dateA = new Date(a.pickupDate).getTime();
          const dateB = new Date(b.pickupDate).getTime();
          return dateB - dateA; // Most recent first
        });
        
        const sortedRentalAgreements = displayRentalAgreements.sort((a, b) => {
          const dateA = new Date(a.pickupDate).getTime();
          const dateB = new Date(b.pickupDate).getTime();
          return dateB - dateA; // Most recent first
        });

        // Detect highlighted item based on contact attributes
        // Priority: capturedNumber > gs_api_reservation_number > gs_api_rental_agreement_number
        const attributes = selectedContact.getAttributes();
        const capturedNumber = attributes?.capturedNumber?.value;
        const resNumber = attributes?.gs_api_reservation_number?.value;
        const raNumber = attributes?.gs_api_rental_agreement_number?.value;

        let detectedHighlight: {type: 'reservation' | 'rental' | null, item: IDisplayReservation | IDisplayRentalAgreement | null} = { type: null, item: null };

        // Check for capturedNumber (could be reservation or rental)
        if (capturedNumber && !detectedHighlight.item) {
          const matchingRes = sortedReservations.find(r => r.number === capturedNumber);
          if (matchingRes) {
            detectedHighlight = { type: 'reservation', item: matchingRes };
          } else {
            const matchingRa = sortedRentalAgreements.find(ra => ra.number === capturedNumber);
            if (matchingRa) {
              detectedHighlight = { type: 'rental', item: matchingRa };
            }
          }
        }

        // Check for reservation number
        if (resNumber && !detectedHighlight.item) {
          const matchingRes = sortedReservations.find(r => r.number === resNumber);
          if (matchingRes) {
            detectedHighlight = { type: 'reservation', item: matchingRes };
          }
        }

        // Check for rental agreement number
        if (raNumber && !detectedHighlight.item) {
          const matchingRa = sortedRentalAgreements.find(ra => ra.number === raNumber);
          if (matchingRa) {
            detectedHighlight = { type: 'rental', item: matchingRa };
          }
        }

        // Update state with detected highlight
        setHighlightedItem(detectedHighlight);

        // Filter out highlighted item from regular lists
        const filteredReservations = detectedHighlight.type === 'reservation'
          ? sortedReservations.filter(r => r.number !== detectedHighlight.item?.number)
          : sortedReservations;
        
        const filteredRentalAgreements = detectedHighlight.type === 'rental'
          ? sortedRentalAgreements.filter(ra => ra.number !== detectedHighlight.item?.number)
          : sortedRentalAgreements;

        setReservations(filteredReservations);
        setRentalAgreements(filteredRentalAgreements);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      logger.error('Error fetching GS Query Results', { error: errorMessage, contactId });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!serviceConfig.useGsQueryResults) {
    return null;
  }

  // Helper function to extract brand from URN
  const extractBrandFromUrn = (brandUrn: string): string => {
    if (!brandUrn) return 'N/A';
    const parts = brandUrn.split(':');
    return parts[parts.length - 1] || 'N/A';
  };

  // Helper function to calculate days until pickup
  const getDaysUntilPickup = (pickupDate: string): { days: number; label: string; color: 'green' | 'orange' | 'red' | 'blue' } => {
    const pickup = new Date(pickupDate);
    const today = new Date();
    const diffTime = pickup.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      return {
        days: diffDays,
        label: `${diffDays}d`,
        color: diffDays <= 1 ? 'red' : diffDays <= 3 ? 'orange' : 'green'
      };
    } else if (diffDays === 0) {
      return { days: 0, label: 'Today', color: 'red' };
    } else {
      return {
        days: Math.abs(diffDays),
        label: `${Math.abs(diffDays)}d`,
        color: 'blue'
      };
    }
  };

  const toggleReservationExpansion = (index: number) => {
    const newExpanded = new Set(expandedReservations);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedReservations(newExpanded);
  };

  const toggleRentalAgreementExpansion = (index: number) => {
    const newExpanded = new Set(expandedRentalAgreements);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRentalAgreements(newExpanded);
  };

  const renderReservations = () => (
    <Card.Content>
      <Card.Header style={{marginBottom: '0.2em'}}>
        <Icon name='calendar check' />
        {config.translate('GSQueryResults_Reservations', agentLang) || 'Reservations'} ({reservations.length})
      </Card.Header>
      {reservations.length === 0 ? (
        <p style={{ color: '#999', fontStyle: 'italic' }}>{config.translate('GSQueryResults_NoReservationsFound', agentLang) || 'No reservations found'}</p>
      ) : (
        <div style={reservations.length >= 3 ? { maxHeight: '400px', overflowY: 'auto' } : {}}>
          {reservations.map((reservation, index) => {
            const isExpanded = expandedReservations.has(index);
            const pickupInfo = getDaysUntilPickup(reservation.pickupDate);
            
            return (
              <Card key={index} style={{ margin: '8px', cursor: 'pointer', width: '90%' }} onClick={() => toggleReservationExpansion(index)}>
                <Card.Content>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Icon name={isExpanded ? 'chevron up' : 'chevron down'} style={{ color: '#666' }} />
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='map marker alternate' />
                      {reservation.stationId}
                    </Label>
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='map marker alternate' />
                      {reservation.gpbr}
                    </Label>
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='tag' />
                      RES
                    </Label>
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='calendar' />
                      {reservation.pickupDate}
                    </Label>
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='building' />
                      {reservation.brand}
                    </Label>
                  </div>
                  
                  {isExpanded && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#666' }}>
                        <div><strong>{config.translate('GSQueryResults_ReservationNumber', agentLang) || 'Reservation #'}:</strong> {reservation.number}</div>
                        <div><strong>{config.translate('GSQueryResults_Customer', agentLang) || 'Customer'}:</strong> {reservation.customerName}</div>
                        <div><strong>{config.translate('GSQueryResults_Station', agentLang) || 'Station'}:</strong> {reservation.stationId}</div>
                        <div><strong>{config.translate('GSQueryResults_GPBR', agentLang) || 'GPBR'}:</strong> {reservation.gpbr}</div>
                        <div><strong>{config.translate('GSQueryResults_PickupDate', agentLang) || 'Pickup Date'}:</strong> {reservation.pickupDate}</div>
                        <div><strong>{config.translate('GSQueryResults_ReturnDate', agentLang) || 'Return Date'}:</strong> {reservation.returnDate}</div>
                        <div><strong>{config.translate('GSQueryResults_Brand', agentLang) || 'Brand'}:</strong> {reservation.brand}</div>
                      </div>
                    </div>
                  )}
                </Card.Content>
              </Card>
            );
          })}
        </div>
      )}
    </Card.Content>
  );

  const renderRentalAgreements = () => (
    <Card.Content>
      <Card.Header style={{marginBottom: '0.2em'}}>
        <Icon name='file alternate outline' />
        {config.translate('GSQueryResults_RentalAgreements', agentLang) || 'Rental Agreements'} ({rentalAgreements.length})
      </Card.Header>
      {rentalAgreements.length === 0 ? (
        <p style={{ color: '#999', fontStyle: 'italic' }}>{config.translate('GSQueryResults_NoRentalAgreementsFound', agentLang) || 'No rental agreements found'}</p>
      ) : (
        <div style={rentalAgreements.length >= 3 ? { maxHeight: '400px', overflowY: 'auto' } : {}}>
          {rentalAgreements.map((agreement, index) => {
            const isExpanded = expandedRentalAgreements.has(index);
            const pickupInfo = getDaysUntilPickup(agreement.pickupDate);
            
            return (
              <Card key={index} style={{ marginBottom: '8px', cursor: 'pointer', width: '100%' }} onClick={() => toggleRentalAgreementExpansion(index)}>
                <Card.Content>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Icon name={isExpanded ? 'chevron up' : 'chevron down'} style={{ color: '#666' }} />
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='map marker alternate' />
                      {agreement.stationId}
                    </Label>
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='map marker alternate' />
                      {agreement.gpbr}
                    </Label>
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='tag' />
                      RA
                    </Label>
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='calendar' />
                      {agreement.pickupDate}
                    </Label>
                    <Label size='small' style={{ backgroundColor: '#e8e8e8', color: '#333' }}>
                      <Icon name='building' />
                      {agreement.brand}
                    </Label>
                  </div>
                  
                  {isExpanded && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#666' }}>
                        <div><strong>{config.translate('GSQueryResults_RANumber', agentLang) || 'RA #'}:</strong> {agreement.number}</div>
                        <div><strong>{config.translate('GSQueryResults_Customer', agentLang) || 'Customer'}:</strong> {agreement.customerName}</div>
                        <div><strong>{config.translate('GSQueryResults_Station', agentLang) || 'Station'}:</strong> {agreement.stationId}</div>
                        <div><strong>{config.translate('GSQueryResults_GPBR', agentLang) || 'GPBR'}:</strong> {agreement.gpbr}</div>
                        <div><strong>{config.translate('GSQueryResults_PickupDate', agentLang) || 'Pickup Date'}:</strong> {agreement.pickupDate}</div>
                        <div><strong>{config.translate('GSQueryResults_ReturnDate', agentLang) || 'Return Date'}:</strong> {agreement.returnDate}</div>
                        <div><strong>{config.translate('GSQueryResults_Brand', agentLang) || 'Brand'}:</strong> {agreement.brand}</div>
                      </div>
                    </div>
                  )}
                </Card.Content>
              </Card>
            );
          })}
        </div>
      )}
    </Card.Content>
  );

  return (
    <Card className="card-jmb" style={{ marginTop: '0px' }}>
      <Card.Content className="header" style={{ cursor: 'pointer' }} onClick={() => setPanelExpanded(!panelExpanded)}>
        <Card.Header textAlign="left">
          <Icon name={panelExpanded ? 'chevron down' : 'chevron right'} />
          <Icon name="search" />
          {config.translate('GSQueryResults_Title', agentLang) || 'Query Results'}
          {!highlightedItem.item && renderVerificationBadge()}
        </Card.Header>
      </Card.Content>
      
      {panelExpanded && (
        <Card.Content>
          {loading && <Loader active inline="centered">{config.translate('GSQueryResults_Loading', agentLang) || 'Loading query results...'}</Loader>}
          {error && (
            <Message negative>
              <Message.Header>{config.translate('GSQueryResults_Error', agentLang) || 'Error'}</Message.Header>
              <p>{config.translate('GSQueryResults_ErrorMessage', agentLang) || 'Unable to retrieve customer information at this time. Please try again or contact support if the issue persists.'}</p>
            </Message>
          )}
          {!loading && !error && (
            <>
              {renderHighlightedItem()}
              {renderReservations()}
              <div style={{ margin: '20px 0', borderBottom: '2px solid #e0e0e0' }}></div>
              {renderRentalAgreements()}
            </>
          )}
        </Card.Content>
      )}
    </Card>
  );
};